/**
 * Hero Judgment registry — extend by roleTags / enemyTags / kitId.
 * Never branch core on `defId === "…"`. Soft≪AA + Claim B bans apply to all.
 *
 * Layers:
 * - HeroKit / RolePolicy / SeatPolicy → personal job+traits (Layer 1–2)
 * - EncounterKnowledge → scenario/enemy tags (not personal traits)
 * - Adjutant seeds stay outside this registry (collective propose; 유대 = follow)
 */
import type { RaidClassId } from "../../../data/raid-catalog";
import { raidCatalog } from "../../../data/raid-catalog";
import type { RaidUnit } from "../raid-types";
import type {
  EncounterKnowledge,
  HeroJudgmentAdapter,
  HeroJudgmentProfile,
  HeroKit,
  HeroRoleTag,
  RolePolicy,
  SeatPolicy,
} from "./hero-architecture";
import type { JudgmentTraits } from "./traits";

const kitsById = new Map<string, HeroKit>();
/** Optional per-hero kit override (catalog quirk) — keyed by roster defId. */
const kitByDefId = new Map<string, HeroKit>();
const rolePolicies = new Map<string, RolePolicy>();
const seatPolicies = new Map<string, SeatPolicy>();
const encounterMods = new Map<string, EncounterKnowledge>();
const adapters: HeroJudgmentAdapter[] = [];

let defaultsSeeded = false;

const ROLE_DEFAULT_KITS: Record<RaidClassId, HeroKit> = {
  healer: {
    kitId: "role.healer.default",
    roleTags: ["healer", "support", "backline"],
    actions: ["heal"],
  },
  tank: {
    kitId: "role.tank.default",
    roleTags: ["tank", "frontline"],
    actions: ["smash", "taunt", "attack"],
  },
  dps: {
    kitId: "role.dps.default",
    roleTags: ["dps", "focus_dps"],
    actions: ["attack", "focus"],
  },
};

/** Insight / bond gates for Layer-2 RolePolicy (ai-profiles.rolePolicy). */
export type RolePolicyTable = {
  healerTriageInsightNormMin: number;
  dpsFocusInsightNormMin: number;
  focusApplyInsightFloor: number;
  focusBondOverrideBase: number;
  focusBondOverrideSlope: number;
  /** Option A Soft-safe 생존 추구 scorer (table-tunable). */
  survivalSeekEnabled: boolean;
  survivalSeekSeatFilterScore: number;
  survivalSeekPredictBlendMax: number;
};

const ROLE_POLICY_FALLBACK: RolePolicyTable = {
  healerTriageInsightNormMin: -0.6,
  dpsFocusInsightNormMin: -0.4,
  focusApplyInsightFloor: -0.35,
  focusBondOverrideBase: 0.15,
  focusBondOverrideSlope: 0.35,
  survivalSeekEnabled: true,
  survivalSeekSeatFilterScore: 10,
  survivalSeekPredictBlendMax: 0.55,
};

export function rolePolicyTable(): RolePolicyTable {
  const raw = (
    raidCatalog.profiles as { rolePolicy?: Partial<RolePolicyTable> }
  ).rolePolicy;
  if (!raw || typeof raw !== "object") return ROLE_POLICY_FALLBACK;
  return {
    healerTriageInsightNormMin:
      typeof raw.healerTriageInsightNormMin === "number"
        ? raw.healerTriageInsightNormMin
        : ROLE_POLICY_FALLBACK.healerTriageInsightNormMin,
    dpsFocusInsightNormMin:
      typeof raw.dpsFocusInsightNormMin === "number"
        ? raw.dpsFocusInsightNormMin
        : ROLE_POLICY_FALLBACK.dpsFocusInsightNormMin,
    focusApplyInsightFloor:
      typeof raw.focusApplyInsightFloor === "number"
        ? raw.focusApplyInsightFloor
        : ROLE_POLICY_FALLBACK.focusApplyInsightFloor,
    focusBondOverrideBase:
      typeof raw.focusBondOverrideBase === "number"
        ? raw.focusBondOverrideBase
        : ROLE_POLICY_FALLBACK.focusBondOverrideBase,
    focusBondOverrideSlope:
      typeof raw.focusBondOverrideSlope === "number"
        ? raw.focusBondOverrideSlope
        : ROLE_POLICY_FALLBACK.focusBondOverrideSlope,
    survivalSeekEnabled:
      typeof raw.survivalSeekEnabled === "boolean"
        ? raw.survivalSeekEnabled
        : ROLE_POLICY_FALLBACK.survivalSeekEnabled,
    survivalSeekSeatFilterScore:
      typeof raw.survivalSeekSeatFilterScore === "number"
        ? raw.survivalSeekSeatFilterScore
        : ROLE_POLICY_FALLBACK.survivalSeekSeatFilterScore,
    survivalSeekPredictBlendMax:
      typeof raw.survivalSeekPredictBlendMax === "number"
        ? raw.survivalSeekPredictBlendMax
        : ROLE_POLICY_FALLBACK.survivalSeekPredictBlendMax,
  };
}

export function registerHeroKit(kit: HeroKit): void {
  kitsById.set(kit.kitId, kit);
}

/** Bind a roster hero defId to a kit (unique skillset). */
export function registerHeroKitForDef(defId: string, kit: HeroKit): void {
  kitByDefId.set(defId, kit);
  registerHeroKit(kit);
}

export function registerRolePolicy(policy: RolePolicy): void {
  rolePolicies.set(policy.id, policy);
}

export function registerSeatPolicy(policy: SeatPolicy): void {
  seatPolicies.set(policy.id, policy);
}

export function registerEncounterKnowledge(mod: EncounterKnowledge): void {
  encounterMods.set(mod.id, mod);
}

export function registerHeroAdapter(adapter: HeroJudgmentAdapter): void {
  if (adapters.some((a) => a.id === adapter.id)) return;
  adapters.push(adapter);
  if (adapter.role) registerRolePolicy(adapter.role);
  if (adapter.seat) registerSeatPolicy(adapter.seat);
}

export function resetHeroJudgmentRegistry(): void {
  kitsById.clear();
  kitByDefId.clear();
  rolePolicies.clear();
  seatPolicies.clear();
  encounterMods.clear();
  adapters.length = 0;
  defaultsSeeded = false;
}

/** Idempotent seed of role-default kits (policies registered by stubs). */
export function ensureDefaultHeroKits(): void {
  if (defaultsSeeded) return;
  for (const kit of Object.values(ROLE_DEFAULT_KITS)) {
    registerHeroKit(kit);
  }
  defaultsSeeded = true;
}

export function stubKitForClass(classId: RaidClassId): HeroKit {
  ensureDefaultHeroKits();
  return ROLE_DEFAULT_KITS[classId];
}

export function kitForUnit(
  u: Pick<RaidUnit, "classId" | "defId">,
): HeroKit {
  ensureDefaultHeroKits();
  const byDef = kitByDefId.get(u.defId);
  if (byDef) return byDef;
  return ROLE_DEFAULT_KITS[u.classId] ?? ROLE_DEFAULT_KITS.dps;
}

export function unitRoleTags(
  u: Pick<RaidUnit, "classId" | "defId">,
): readonly HeroRoleTag[] {
  return kitForUnit(u).roleTags;
}

export function unitHasRoleTag(
  u: Pick<RaidUnit, "classId" | "defId">,
  tag: HeroRoleTag,
): boolean {
  return unitRoleTags(u).includes(tag);
}

/** Any ally that can staff-heal (healer or support tag). */
export function unitIsStaffHealer(
  u: Pick<RaidUnit, "classId" | "defId" | "hp">,
): boolean {
  if (u.hp <= 0) return false;
  return unitHasRoleTag(u, "healer") || unitHasRoleTag(u, "support");
}

function tagsOverlap(
  a: readonly string[],
  b: readonly string[],
): boolean {
  if (a.length === 0) return true;
  for (const t of a) {
    if (b.includes(t)) return true;
  }
  return false;
}

export function rolePoliciesForKit(kit: HeroKit): RolePolicy[] {
  const tags = kit.roleTags as readonly string[];
  return [...rolePolicies.values()].filter((p) =>
    tagsOverlap(p.roleTags as readonly string[], tags),
  );
}

export function seatPoliciesForKit(kit: HeroKit): SeatPolicy[] {
  const tags = kit.roleTags as readonly string[];
  return [...seatPolicies.values()].filter(
    (p) =>
      p.roleTags.length === 0 ||
      tagsOverlap(p.roleTags as readonly string[], tags),
  );
}

export function encounterModulesMatching(
  enemyTags: readonly string[],
): EncounterKnowledge[] {
  return [...encounterMods.values()].filter((m) =>
    tagsOverlap(m.enemyTags as readonly string[], enemyTags as string[]),
  );
}

export function adaptersMatching(
  hero: RaidUnit,
  kit: HeroKit,
): HeroJudgmentAdapter[] {
  return adapters.filter((a) => a.matches(hero, kit));
}

export function composeHeroJudgmentProfile(
  hero: RaidUnit,
  traits: JudgmentTraits,
): HeroJudgmentProfile {
  const kit = kitForUnit(hero);
  const hit = adaptersMatching(hero, kit);
  return {
    traits,
    kit,
    seatPolicies: hit.map((a) => a.seat).filter((s): s is SeatPolicy => !!s),
    rolePolicies: hit.map((a) => a.role).filter((r): r is RolePolicy => !!r),
    encounterModules: [...encounterMods.values()],
    polar: undefined,
  };
}

/** @internal test / harness */
export function _registrySnapshot() {
  return {
    kitIds: [...kitsById.keys()],
    rolePolicyIds: [...rolePolicies.keys()],
    encounterIds: [...encounterMods.keys()],
    adapterIds: adapters.map((a) => a.id),
  };
}
